import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/react/shadcn/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "web3kit-fixed web3kit-inset-0 web3kit-z-20 web3kit-bg-white/20 data-[state=open]:web3kit-animate-in data-[state=closed]:web3kit-animate-out data-[state=closed]:web3kit-fade-out-0 data-[state=open]:web3kit-fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    className?: string;
  }
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className="DialogContent" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "web3kit-fixed web3kit-left-[50%] web3kit-top-[50%] web3kit-z-20 web3kit-grid web3kit-w-full web3kit-max-w-lg web3kit-translate-x-[-50%] web3kit-translate-y-[-50%] web3kit-gap-4 web3kit-border web3kit-bg-background web3kit-p-6 web3kit-shadow-lg web3kit-duration-200 data-[state=open]:web3kit-animate-in data-[state=closed]:web3kit-animate-out data-[state=closed]:web3kit-fade-out-0 data-[state=open]:web3kit-fade-in-0 data-[state=closed]:web3kit-zoom-out-95 data-[state=open]:web3kit-zoom-in-95 data-[state=closed]:web3kit-slide-out-to-left-1/2 data-[state=closed]:web3kit-slide-out-to-top-[48%] data-[state=open]:web3kit-slide-in-from-left-1/2 data-[state=open]:web3kit-slide-in-from-top-[48%] sm:web3kit-rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="web3kit-absolute web3kit-right-4 web3kit-top-4 web3kit-rounded-sm web3kit-opacity-70 web3kit-ring-offset-background web3kit-transition-opacity hover:web3kit-opacity-100 focus:web3kit-outline-none focus:web3kit-ring-2 focus:web3kit-ring-ring focus:web3kit-ring-offset-2 disabled:web3kit-pointer-events-none data-[state=open]:web3kit-bg-accent data-[state=open]:web3kit-text-muted-foreground">
        <X className="web3kit-h-4 web3kit-w-4" />
        <span className="web3kit-sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { className?: string }
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "web3kit-flex web3kit-flex-col web3kit-space-y-1.5 web3kit-text-center sm:web3kit-text-left",
      className
    )}
    {...props}
  />
));
DialogHeader.displayName = "DialogHeader";

const DialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { className?: string }
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "web3kit-flex web3kit-flex-col-reverse sm:web3kit-flex-row sm:web3kit-justify-end sm:web3kit-space-x-2",
      className
    )}
    {...props}
  />
));
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "web3kit-text-lg web3kit-font-semibold web3kit-leading-none web3kit-tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
    className?: string;
  }
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("web3kit-text-sm web3kit-text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
